export const DB_NAME = 'two-little-tickets';
export const DB_VERSION = 2;
export const STORE_NAMES = ['space', 'tickets', 'imports', 'images', 'backupConflicts'] as const;

export type StoreName = typeof STORE_NAMES[number];

export type StoredImage =
  | { id: string; bytes: ArrayBuffer; type: string }
  | { id: string; blob: Blob };

export interface ImportMarker {
  id: string;
}

export async function openAppDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = factory.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains('space')) database.createObjectStore('space', { keyPath: 'coupleId' });
      if (!database.objectStoreNames.contains('tickets')) database.createObjectStore('tickets', { keyPath: 'id' });
      if (!database.objectStoreNames.contains('imports')) database.createObjectStore('imports', { keyPath: 'id' });
      if (!database.objectStoreNames.contains('images')) database.createObjectStore('images', { keyPath: 'id' });
      if (!database.objectStoreNames.contains('backupConflicts')) database.createObjectStore('backupConflicts', { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('failed to open local storage'));
    request.onblocked = () => reject(new Error('local storage upgrade was blocked'));
  });
}

export function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('local storage request failed'));
  });
}

export async function runTransaction<T>(
  factory: IDBFactory,
  stores: StoreName | StoreName[],
  mode: IDBTransactionMode,
  operation: (transaction: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  const database = await openAppDatabase(factory);
  try {
    const transaction = database.transaction(stores, mode);
    const completion = transactionCompletion(transaction);
    try {
      const result = await operation(transaction);
      await completion;
      return result;
    } catch (error) {
      try {
        transaction.abort();
      } catch {
        // A request error may already have aborted the transaction.
      }
      try {
        await completion;
      } catch {
        // Preserve the request or application error that caused the abort.
      }
      throw error;
    }
  } finally {
    database.close();
  }
}

function transactionCompletion(transaction: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('local storage transaction failed'));
    transaction.onabort = () => reject(transaction.error ?? new DOMException('local storage transaction aborted', 'AbortError'));
  });
}
